import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import Header from './Header/Header';
import Sidebar from './Sidebar/Sidebar';
import TodayOrders from './TodayOrders/TodayOrders';
import WeeklyRevenue from './WeeklyRevenue/WeeklyRevenue';
import TotalSpent from './TotalSpent/TotalSpent';
import NewUsers from './NewUsers/NewUsers';
import StaffTable from './StaffTable/StaffTable';
import CustomersPage from './Customers/CustomersPage';
import PieChartComponent from './PieChart/PieChart';
import RecentActivity from './RecentActivity/RecentActivity';
import UpcomingTasks from './UpcomingTasks/UpcomingTasks';
import StaffMessages from '../Staff/StaffMessages';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { API_CONFIG } from '../../utils/apiConfig';

// --- Dynamic Product Info Component ---
const ProductInfo = () => {
  const [latestProduct, setLatestProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestProduct = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setLatestProduct(null);
          return;
        }

        const response = await axios.get(`${API_CONFIG.BASE_URL}/api/products`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const products = Array.isArray(response.data) ? response.data : (response.data.data || []);
        
        if (products.length > 0) {
          // Sort by creation date and get the latest
          const sortedProducts = products.sort((a, b) => 
            new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0)
          );
          setLatestProduct(sortedProducts[0]);
        }
      } catch (error) {
        console.error('Error fetching latest product:', error);
        // Set fallback data when API is unavailable
        setLatestProduct({
          _id: '1',
          name: 'Sample Product',
          price: 100,
          category: 'General',
          createdAt: new Date().toISOString()
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLatestProduct();
  }, []);

  return (
    <div className="card product-info">
      <h4>LATEST PRODUCT</h4>
      {loading ? (
        <p>Loading...</p>
      ) : latestProduct ? (
        <div className="product-details">
          <p className="product-name">{latestProduct.itemName || 'Unnamed Product'}</p>
          <p className="product-code">Code: {latestProduct.itemCode || 'N/A'}</p>
          <p className="product-price">
            ₹{latestProduct.unitPrice ? latestProduct.unitPrice.toLocaleString() : '0'}
          </p>
        </div>
      ) : (
        <p>No products found</p>
      )}
    </div>
  );
};

// --- Placeholder components for missing widgets ---
const GaugeCard = ({ title, value, percent }) => (
  <div className="card gauge-card">
    <h4>{title}</h4>
    <div className="gauge">
      <div className="gauge-fill" style={{ width: `${percent}%` }} />
      <span className="gauge-value">{value}</span>
    </div>
  </div>
);

const NPSBreakdown = () => (
  <div className="card nps-breakdown">
    <h4>NPS Breakdown</h4>
    <div className="nps-row">
      <span className="nps-promoter">😊 67.76%<br />Promoters</span>
      <span className="nps-passive">😐 20.43%<br />Passives</span>
      <span className="nps-detractor">😡 11.81%<br />Detractors</span>
    </div>
  </div>
);




const Dashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [refreshTrigger] = useState(0);
  const { currentUser, loading } = useAuth();
  
  // Handle company selection
  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
  };

  
  const toggleSidebar = () => {

    setSidebarOpen(!sidebarOpen);
  };
  
  // Handle window resize
  const handleResize = () => {
    if (window.innerWidth > 768) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="auth-loading">
          <h2>Checking authentication...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Show authentication error if not authenticated
  if (!currentUser) {
    return (
      <div className="dashboard-container">
        <div className="auth-error">
          <h2>Authentication Required</h2>
          <p>You need to be logged in to access the dashboard.</p>
          <p>Redirecting to login page...</p>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <button className="sidebar-toggle-btn" onClick={toggleSidebar}>
        {sidebarOpen ? '✕' : '☰'}
      </button>
      <Sidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        onCompanySelect={handleCompanySelect}
        selectedCompany={selectedCompany}
        refreshTrigger={refreshTrigger}
      />
      
      <div className={`main-cont ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <Header />
        
        {/* Dashboard Header */}
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>📊 Dashboard Overview</h1>
            <p>Welcome back! Here's what's happening with your business today.</p>
          </div>
          <div className="dashboard-stats">
            <div className="stat-item">
              <span className="stat-label">Last Updated</span>
              <span className="stat-value">{new Date().toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
        
        <div className="dashboard-container">
          {/* Only render components if authenticated */}
          {currentUser && (
            <>
              {/* First Row - Key Performance Indicators (3 cards) */}
              <div className="kpi-row">
                <div className="grid-item total-spent">
                  <div className="card kpi-card">
                    <h4>💰 Today's Profit</h4>
                    <TotalSpent />
                  </div>
                </div>
                <div className="grid-item avg-response">
                  <div className="card kpi-card">
                    <h4>📦 Today's Orders</h4>
                    <TodayOrders />
                  </div>
                </div>
                <div className="grid-item weekly-revenue">
                  <div className="card kpi-card">
                    <h4>📈 Weekly Revenue</h4>
                    <WeeklyRevenue />
                  </div>
                </div>
              </div>
              
              {/* Second Row - Activity & Analytics (3 cards) */}
              <div className="activity-row">
                <div className="grid-item recent-activity">
                  <div className="card activity-card">
                    <h4>📋 Recent Activity</h4>
                    <RecentActivity />
                  </div>
                </div>
                <div className="grid-item product">
                  <div className="card product-card">
                    <h4>🛍️ Latest Product</h4>
                    <ProductInfo />
                  </div>
                </div>
                <div className="grid-item pie-chart">
                  <div className="card analytics-card">
                    <h4>📊 Bill Distribution</h4>
                    <PieChartComponent />
                  </div>
                </div>
              </div>
              
              {/* Third Row - Customer Satisfaction & Performance (3 cards) */}
              <div className="analytics-row">
                <div className="grid-item csat-score">
                  <div className="card performance-card">
                    <h4>🎯 CSAT Score</h4>
                    <GaugeCard title="CSAT Score" value="85%" percent={85} />
                  </div>
                </div>
                <div className="grid-item nps-breakdown">
                  <div className="card satisfaction-card">
                    <h4>😊 Customer Satisfaction</h4>
                    <NPSBreakdown />
                  </div>
                </div>
                <div className="grid-item nps-gauge">
                  <div className="card gauge-card">
                    <NewUsers />
                  </div>
                </div>
              </div>
              
              {/* Fourth Row - Management & Operations (3 cards) */}
              <div className="management-row">
                <div className="grid-item staff-table">
                  <div className="card management-card">
                    <h4>👨‍💼 Staff Management</h4>
                    <StaffTable />
                  </div>
                </div>
                <div className="grid-item new-users">
                  <div className="card customer-card">
                    <CustomersPage />
                  </div>
                </div>
                <div className="grid-item upcoming-tasks">
                  <div className="card tasks-card">
                    <h4>✅ Upcoming Tasks</h4>
                    <UpcomingTasks />
                  </div>
                </div>
              </div>
              
              {/* Fifth Row - Messages */}
              <div className="messages-row">
                <div className="grid-item messages">
                  <div className="card messages-card">
                    <h4>📨 Messages from Marketing Admin</h4>
                    <StaffMessages />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;